<template>
    <div class="relative">
        <input
            class="text-left block w-full bg-white rounded border border-slate-200 py-1.5 pr-8 text-gray-900 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            :class="[error ? 'border-red-500' : '', $attrs.class]"
            :id="id"
            :placeholder="placeholder"
            ref="input"
            v-model="textInput"
            type="text"
            @keydown.enter.prevent="chooseActivePlace"
            @keydown.down="down"
            @keydown.up="up"
            @input="search"
            @focus="$emit('onFocus')"
            autocomplete="off"
            autofocus
            v-show="!chosenPlace"
        />
        <div
            class="text-left block w-full bg-white rounded border border-slate-200 py-1.5 pr-8 pl-4 text-gray-900 placeholder:text-slate-500 placeholder:text-sm disabled:opacity-50"
            v-if="chosenPlace"
        >
            {{ chosenPlace }}
        </div>
        <div
            v-if="chosenPlace"
            class="absolute top-0 left-3 right-0 py-1.5 text-gray-900 z-10"
        >
            <div class="relative w-full">
                <!--{{ chosenPlace.displayName }} {{ chosenPlace.formattedAddress }} -->
                <button
                    type="button"
                    class="absolute top-0 right-2 text-2xl px-1 text-red-700 leading-none hover:text-red-500 transition-colors"
                    @click="clearPlace()"
                >
                    &times;
                </button>
            </div>
        </div>
        <transition name="fade">
            <ul
                class="block w-full max-h-64 overflow-y-auto bg-white rounded border border-slate-200 shadow absolute top-10 left-0 z-30 p-1"
                v-if="places.length > 0"
            >
                <li
                    v-for="place in places"
                    :key="place"
                    @click="selectPlace(place)"
                    class="rounded px-4 py-1.5 text-slate-900 hover:bg-slate-100 cursor-pointer transition-colors"
                    :class="{
                        'bg-white': !place.active,
                        'bg-slate-200': place.active,
                    }"
                >
                    {{ place.formattedAddress }}
                </li>
            </ul>
        </transition>
    </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';

const emit = defineEmits(['placeChanged', 'placeCleared', 'onFocus']);

const props = defineProps({
    types: {
        type: Array,
        default: () => [],
    },
    placeholder: {
        type: String,
        default: '',
    },
    id: {
        type: String,
        default: '',
    },
    currentPlace: {
        type: Object,
        default: null,
    },
    error: {},
});

const input = ref(null);

const places = ref([]);
const activeIndex = ref(-1);
const searchInput = ref('');

const textInput = computed({
    get() {
        return props.currentPlace && props.currentPlace.formattedAddress
            ? props.currentPlace.formattedAddress
            : '';
    },
    set(value) {
        searchInput.value = value;
    },
});

const chosenPlace = ref(null);

/*
watch(
    () => props.currentPlace,
    (newValue) => {
        if (newValue) {
            chosenPlace.value = newValue.formattedAddress;
        }
    },
    { immediate: true }
);
*/
const { AutocompleteSessionToken, AutocompleteSuggestion, Place } =
    await google.maps.importLibrary('places');

const search = async () => {
    if (searchInput.value === '') return;
    places.value = [];

    let request = {
        input: searchInput.value,
        includedPrimaryTypes: props.types,
        language: 'en-US',
    };

    const token = new AutocompleteSessionToken();

    request.sessionToken = token;

    const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    const promises = suggestions.map(async (s) => {
        let place = new Place({ id: s.placePrediction.placeId });

        await place.fetchFields({
            fields: [
                'displayName',
                'formattedAddress',
                'addressComponents',
                'location',
            ],
        });

        let addressByTypes = {};
        for (var i = 0; i < place.addressComponents.length; i++) {
            let c = place.addressComponents[i];
            addressByTypes[c.types[0]] = {
                shortName: c.shortText,
                longName: c.longText,
            };
        }

        return {
            displayName: place.displayName,
            formattedAddress: place.formattedAddress,
            lat: place.location.lat(),
            lng: place.location.lng(),
            addressTypes: addressByTypes,
            active: false,
        };
    });

    places.value = await Promise.all(promises);
};

const selectPlace = (place) => {
    chosenPlace.value = place.formattedAddress;
    textInput.value = place.formattedAddress;
    places.value = [];
    activeIndex.value = -1;
    emit('placeChanged', place);
};

const chooseActivePlace = () => {
    textInput.value = places.value[activeIndex.value].formattedAddress;
    chosenPlace.value = places.value[activeIndex.value].formattedAddress;
    emit('placeChanged', places.value[activeIndex.value]);
    places.value = [];
    activeIndex.value = -1;
};

const clearPlace = () => {
    places.value = [];
    textInput.value = '';
    chosenPlace.value = null;
    searchInput.value = '';
    activeIndex.value = -1;
    input.value.focus();
    emit('placeCleared');
};

const up = () => {
    if (activeIndex.value > 0) {
        if (activeIndex.value > 0) {
            activeIndex.value--;
        }
        resetActiveItem();
        places.value[activeIndex.value].active = true;
    }
};

const down = () => {
    if (activeIndex.value + 1 < places.value.length) {
        activeIndex.value++;
        resetActiveItem();
        places.value[activeIndex.value].active = true;
    }
};

const resetActiveItem = () => {
    places.value.forEach((item) => {
        item.active = false;
    });
};
</script>
